/**
 * Whose birthday it is today, at the top of «Спевки» — T17 of the F-004 plan.
 *
 * Ported from `legacy/frontend/src/screens/BirthdayCard.tsx` at legacy commit 2953e3f:
 * every comment, the declaration order, the words. What is below is the old file's own,
 * from its line 4.
 *
 * **This file can never join `scripts/verbatim.txt`**, and for the one reason `format.ts`
 * cannot. Legacy's line 3 is `import { api, type BirthdayToday } from '../api'`, and here
 * that line has to be two: `BirthdayToday` is a server shape and nothing outside
 * `packages/contracts/` may define one (ADR-0011), while `api` is the client's own object.
 * A value import cannot share a line with a type-only import, so the head of the file is
 * ours and the body is theirs. `scripts/ported-partial.txt` records where the seam is and
 * `scripts/check-css-port.sh` holds everything under it to the original — FND-034.
 *
 * The seam is at legacy line 4 rather than at line 2, which is where `format.ts`'s is: the
 * old file's react import is line 1 and the import that has to split is line 3, so the two
 * lines between them are ours as well.
 */

import { useCallback, useEffect, useState } from 'react';

import type { BirthdayToday } from '@contracts/index';

import { api } from '../api';
import { voicePartName } from '../format';
import { CakeIcon } from '../icons';
import { useWake } from '../liveness';
import Avatar, { AVATAR_ROW } from './Avatar';
import Unfold from './Unfold';

/**
 * Whose birthday it is today, at the top of «Спевки».
 *
 * On this screen and no other, by the owner's call of 2026-08-19: it is the one
 * everybody opens without meaning to, and a congratulation nobody walks past is
 * the whole point. In the roster on «Хор» it would be found only by somebody
 * who came looking, which is not how anybody remembers a birthday.
 *
 * Reloaded on waking rather than polled. The answer changes once a day, at
 * midnight in the choir's own zone, and the app spends most of that day in a
 * pocket - so the moment worth re-asking at is the moment it comes back, which
 * is exactly what useWake is.
 *
 * Nothing is drawn when nobody has one, which is most days: a card saying "no
 * birthdays today" is a card in the way three hundred and sixty times a year.
 */
export default function BirthdayCard() {
  const [rows, setRows] = useState<BirthdayToday[]>([]);

  const load = useCallback(async () => {
    try {
      setRows(await api.birthdaysToday());
    } catch {
      // A greeting that cannot be loaded is simply not drawn. Nothing on this
      // screen depends on it, and a failure line here would be noise over the
      // rehearsal somebody actually opened the app for.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useWake(() => void load());

  // C-023 batch 2 (`FND-1618`): the greeting comes in after the schedule has been drawn, and
  // it stands above everything, so arriving whole it shoved the whole screen down in one frame
  // (134 px on the audit's rig). It grows in instead; on the days it has nothing it draws
  // nothing, as before.
  return (
    <Unfold open={rows.length > 0}>
      <section className="card card--toned">
      <p className="card-when">
        <CakeIcon size={18} />
        СЕГОДНЯ ДЕНЬ РОЖДЕНИЯ
      </p>
      <ul className="live">
        {rows.map((row) => (
          <li className="live-row" key={row.member_id}>
            {/* Smiling its widest whatever the last month's attendance says:
                this is the one row in the app where the face is about the day
                rather than about the figures, and since the smile left the
                lists it is the only one in a list that says anything at all. */}
            <Avatar
              eyes={row.avatar_eyes}
              color={row.avatar_color}
              celebrating
              size={AVATAR_ROW}
              mine={row.is_me}
            />
            <span className="live-body">
              <span className="live-name">{row.display_name}</span>
              <span className="live-sub">
                {row.is_me ? 'поздравляем!' : voicePartName(row.voice_part)}
              </span>
            </span>
          </li>
        ))}
      </ul>
      </section>
    </Unfold>
  );
}
